import React from 'react';

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

/**
 * ErrorBoundary que captura erros de carregamento de chunk (React.lazy).
 * Após um novo deploy, os hashes dos chunks mudam e o browser tenta
 * carregar arquivos que não existem mais. A solução é recarregar a página.
 */
export class ChunkErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed/i.test(error.message) ||
      /Failed to fetch dynamically imported module/i.test(error.message) ||
      /Importing a module script failed/i.test(error.message);

    return { hasError: true, isChunkError };
  }

  componentDidCatch(error: Error) {
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed/i.test(error.message) ||
      /Failed to fetch dynamically imported module/i.test(error.message) ||
      /Importing a module script failed/i.test(error.message);

    if (isChunkError) {
      // Aguarda um tick para garantir que o estado foi salvo antes de recarregar
      setTimeout(() => window.location.reload(), 100);
    }
  }

  render() {
    if (this.state.hasError && !this.state.isChunkError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
          <div className="text-center p-8">
            <p className="text-gray-700 dark:text-gray-300 mb-4">Algo deu errado ao carregar a página.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Recarregar
            </button>
          </div>
        </div>
      );
    }

    if (this.state.hasError && this.state.isChunkError) {
      // Mostra loader enquanto recarrega automaticamente
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Atualizando aplicação...</span>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
